import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import PurchaseReturnModal from "../../core/modals/purchases/PurchaseReturnModal";
import { fetchPurchaseReturns, deletePurchaseReturnThunk } from "../../core/redux/slices/purchaseSlice";
import { getApiErrorMessage } from "../../core/utils/apiError";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—";

const PurchaseReturnList = () => {
  const dispatch = useDispatch();
  const { returns, loading } = useSelector((s) => s.purchases);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => { dispatch(fetchPurchaseReturns()); }, [dispatch]);
  useEffect(() => { load(); }, [load]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return returns.filter(
      (r) => !q
        || r.purchaseReturnNo?.toLowerCase().includes(q)
        || r.supplierName?.toLowerCase().includes(q)
        || r.remarks?.toLowerCase().includes(q)
    );
  }, [returns, search]);

  const handleDelete = (record) => {
    const id = record.purchaseReturnID ?? record.id;
    Swal.fire({
      title: "Delete Purchase Return?",
      text: `Return ${record.purchaseReturnNo || id} will be removed and stock quantities restored.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await dispatch(deletePurchaseReturnThunk(id)).unwrap();
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Return deleted", showConfirmButton: false, timer: 2000 });
      } catch (err) {
        Swal.fire("Error", getApiErrorMessage(err, "Failed to delete purchase return."), "error");
      }
    });
  };

  const columns = [
    {
      title: "Return No", dataIndex: "purchaseReturnNo", width: 160,
      sorter: (a, b) => (a.purchaseReturnNo || "").localeCompare(b.purchaseReturnNo || ""),
    },
    {
      title: "Return Date", dataIndex: "returnDate", width: 120,
      render: fmtDate,
      sorter: (a, b) => (a.returnDate || "").localeCompare(b.returnDate || ""),
    },
    { title: "Supplier", dataIndex: "supplierName" },
    {
      title: "Total Amount", dataIndex: "totalAmount", align: "right", width: 140,
      render: fmt,
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
    },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
    {
      title: "", dataIndex: "action", width: 60, align: "center",
      render: (_, record) => (
        <button className="btn btn-sm text-danger p-1" title="Delete" onClick={() => handleDelete(record)}>
          <Trash2 size={15} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Purchase Returns" subtitle="Manage goods returned to suppliers" onAdd={() => setShowModal(true)} addLabel="New Return" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search return no, supplier or remarks..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={load}>Refresh</button>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <PurchaseReturnModal show={showModal} onClose={() => { setShowModal(false); load(); }} />
    </div>
  );
};

export default PurchaseReturnList;
